// @flow

import * as React from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { View } from 'reshaped';

import { Button } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { CheckboxField } from 'src/Reshaped/Form/CheckboxField';
import { PillsField } from 'src/Reshaped/Form/PillsField';
import { SwitchField } from 'src/Reshaped/Form/SwitchField';
import { TextField } from 'src/Reshaped/Form/TextField';
import { CountrySelect } from 'src/components/Form/CountrySelect';
import { Email } from 'src/components/Form/Email';

import { Phone } from '../../../ContactForm/components/Phone';
import { StateSelect } from '../../../ContactForm/components/StateSelect';
import { Zip } from '../../../ContactForm/components/Zip';

import { InputField } from './InputField';
import { SelectField } from './SelectField';
import type { ContactMode, FormValues } from './types';
import { useContactSubmitHandlers } from './useContactSubmitHandlers';

export type NamedValue = {
  value: any,
  label: string,
};

export type FieldRenderer = (
  // eslint-disable-next-line no-use-before-define
  field: FieldSchema,
  formProps: any,
  children?: Array<React.Node>,
) => React.Node;

export type FieldSchema = {
  name: string,
  type: string,
  required?: boolean,
  label?: string,
  sublabel?: React.Node,
  help?: React.Node,
  renderer?: FieldRenderer,
  values?: Array<NamedValue>,
  isReadOnly?: boolean,
  children?: Array<FieldSchema>,
  placeholder?: string,
};

// The supported field types.
const defaultRenderers: {
  +[string]: FieldRenderer,
  ...
} = {
  form__root: (field, children: Array<React.Node> = []) => children,
  form__section: (field, children) => (
    <View key={field.name} className="wrapper-md" gap={6}>
      {children}
    </View>
  ),
  boolean: field => (
    <InputField field={field}>
      {({ name, label, control, disabled, rules, help, sublabel }) => (
        <CheckboxField
          key={name}
          name={name}
          label={label}
          help={help}
          control={control}
          disabled={disabled}
          rules={rules}
        >
          {sublabel}
        </CheckboxField>
      )}
    </InputField>
  ),
  string: field => (
    <InputField field={field}>
      {({ name, label, control, rules, placeholder, help }) => (
        <TextField
          name={name}
          label={label}
          control={control}
          rules={rules}
          inputProps={{ placeholder }}
          help={help}
        />
      )}
    </InputField>
  ),
  oneOf: field => (
    <InputField field={field}>
      {({
        children,
        renderer,
        sublabel,
        type,
        placeholder,
        required,
        ...fieldProps
      }) => <PillsField size="small" {...fieldProps} />}
    </InputField>
  ),
  email: field => (
    <InputField field={field}>
      {({
        children,
        isReadOnly,
        renderer,
        sublabel,
        type,
        values,
        ...fieldProps
      }) => <Email {...fieldProps} />}
    </InputField>
  ),
  country: field => (
    <SelectField field={field}>
      {({
        children,
        isReadOnly,
        renderer,
        sublabel,
        type,
        values,
        ...fieldProps
      }) => <CountrySelect {...fieldProps} />}
    </SelectField>
  ),
  state: field => (
    <SelectField field={field}>
      {({
        children,
        isReadOnly,
        renderer,
        sublabel,
        type,
        values,
        ...fieldProps
      }) => <StateSelect {...fieldProps} />}
    </SelectField>
  ),
  zip: field => (
    <InputField field={field}>
      {({
        children,
        isReadOnly,
        renderer,
        sublabel,
        type,
        values,
        ...fieldProps
      }) => <Zip {...fieldProps} />}
    </InputField>
  ),
  phone: field => (
    <InputField field={field}>
      {({
        children,
        isReadOnly,
        renderer,
        sublabel,
        type,
        values,
        ...fieldProps
      }) => <Phone {...fieldProps} />}
    </InputField>
  ),
};

const renderFormContent = (
  form: FieldSchema,
  ctx: { contactType: ?string },
): any => {
  if (form.name === 'orgname' && ctx.contactType === 'individual') {
    return null;
  }
  const renderer = form.renderer || defaultRenderers[form.type];
  return (
    renderer &&
    renderer(
      form,
      (form.children || []).map(child => renderFormContent(child, ctx)),
    )
  );
};

export type Props = {
  formView: any,
  cancelLabel: string,
  submitLabel: string,
  isWorking: boolean,
  canWriteContactBook: boolean,
  initialValues: FormValues,
  mode: ContactMode,
};

export function DomainContactForm({
  formView,
  cancelLabel,
  submitLabel,
  isWorking,
  canWriteContactBook,
  initialValues,
  mode,
}: Props): React.Node {
  const { __ } = useTranslator();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const contactType = form.watch('type');

  // If we switch to "individual", orgname no longer makes sense:
  // we clean up the value so as not to send it to the submit when
  // it is no longer visible.
  React.useEffect(() => {
    if (contactType === 'individual') {
      form.setValue('orgname', '');
    }
  }, [contactType, form]);

  const submitHandlers = useContactSubmitHandlers(form, mode);

  return (
    <FormProvider {...form}>
      {/* $FlowFixMe[prop-missing] */}
      <View
        as="form"
        attributes={{
          id: mode,
          onSubmit: form.handleSubmit(submitHandlers),
        }}
        paddingBottom={4}
        gap={8}
      >
        {renderFormContent(formView, { contactType })}

        <SwitchField
          key="saveToContactbook"
          name="saveToContactbook"
          control={form.control}
          disabled={!canWriteContactBook}
        >
          {__('Save to contact book')}
        </SwitchField>
        <View wrap gap={{ s: 3, m: 5 }} direction={{ m: 'row' }}>
          <Button
            size="large"
            onClick={() => navigate(-1)}
            variant="outline"
            disabled={isWorking}
          >
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            size="large"
            loading={isWorking}
            color="primary"
          >
            {submitLabel}
          </Button>
        </View>
      </View>
    </FormProvider>
  );
}
